import { Checkbox, FormControl, FormErrorMessage } from '@chakra-ui/react';
import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Combobox, DateInput, Input } from 'shared-components';
import { DropdownOption } from '../types';
import { IAgentRegistrationForm } from '../hooks/useApplicationFrom/useAgentForm';
import { IProviderRegistrationForm, IUniStaffRegistrationForm } from '../hooks/useApplicationFrom';

type RegistationForm = IAgentRegistrationForm | IUniStaffRegistrationForm | IProviderRegistrationForm;

interface BaseFieldProps {
  control: Control<RegistationForm>;
  errors: FieldErrors<RegistationForm>;
  isSubmitted: boolean;
}

interface TextInputFieldProps extends BaseFieldProps {
  name: keyof RegistationForm;
  label: string;
  type?: string;
}
interface SilentApplicationCheckboxProps extends BaseFieldProps {
  name: keyof RegistationForm;
  disabled?: boolean;
}

export const TextInputField: React.FC<TextInputFieldProps> = ({ control, errors, name, label, type = 'text' }) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { name: fieldName, value, onChange } }) => (
      <Input
        id={fieldName}
        name={fieldName}
        type={type}
        label={label}
        value={String(value) || ''}
        onChange={(evt: React.FormEvent<HTMLInputElement>) => onChange(evt.currentTarget.value)}
        errorText={errors[name]?.message}
      />
    )}
  />
);

interface ComboboxFieldProps extends BaseFieldProps {
  name: keyof RegistationForm;
  label: string;
  options: DropdownOption[];
}

export const ComboboxField: React.FC<ComboboxFieldProps> = ({ control, errors, name, label, options }) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { name: fieldName, value, onChange } }) => (
      <Combobox
        id={fieldName}
        name={fieldName}
        label={label}
        options={options}
        value={[String(value) || '']}
        onChange={(newValue: { value: string }) => onChange(newValue.value)}
        size="lg"
        errorText={errors[name]?.message}
        selectProps={{ isInvalid: !!errors[name]?.message }}
      />
    )}
  />
);

interface DateInputFieldProps extends BaseFieldProps {
  name: keyof RegistationForm;
  label: string;
}

export const DateInputField: React.FC<DateInputFieldProps> = ({ control, errors, isSubmitted, name, label }) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { name: fieldName, value, onChange } }) => {
      const dateValue = String(value) || '';
      const [YYYY, MM, DD] = dateValue.split('-');

      return (
        <DateInput
          id={fieldName}
          name={fieldName}
          label={label}
          dayName={`${fieldName}Day`}
          monthName={`${fieldName}Month`}
          yearName={`${fieldName}Year`}
          value={{
            YYYY: YYYY || '',
            MM: MM || '',
            DD: DD || '',
          }}
          errorText={isSubmitted ? errors[name]?.message : undefined}
          onChangeDD={(evt: React.FormEvent<HTMLInputElement>) => {
            const newDD = evt.currentTarget.value;
            onChange([YYYY || '', MM || '', newDD].join('-'));
          }}
          onChangeMM={(evt: React.FormEvent<HTMLInputElement>) => {
            const newMM = evt.currentTarget.value;
            onChange([YYYY || '', newMM, DD || ''].join('-'));
          }}
          onChangeYY={(evt: React.FormEvent<HTMLInputElement>) => {
            const newYYYY = evt.currentTarget.value;
            onChange([newYYYY, MM || '', DD || ''].join('-'));
          }}
          inputPropsDD={{ autoComplete: 'bday-day' }}
          inputPropsMM={{ autoComplete: 'bday-month' }}
          inputPropsYY={{ autoComplete: 'bday-year' }}
        />
      );
    }}
  />
);

interface ConsentCheckboxProps {
  value: boolean;
  name: string;
  onChange: (value: boolean) => void;
  error?: string;
  disabled?: boolean;
}

export const ConsentCheckbox: React.FC<ConsentCheckboxProps> = ({ value, onChange, error, name }) => (
  <FormControl isRequired isInvalid={!!error}>
    <Checkbox
      id="agentConsent"
      name={name}
      size="lg"
      isChecked={value}
      variant="outline"
      width="100%"
      onChange={e => onChange(e.currentTarget.checked)}
    >
      You confirm that you have been authorised by the applicant to prepare, submit, and manage this application on
      their behalf and to share their personal data as required for these purposes.
    </Checkbox>
    <FormErrorMessage>{error}</FormErrorMessage>
  </FormControl>
);

export const SilentApplicationCheckbox: React.FC<SilentApplicationCheckboxProps> = ({
  control,
  errors,
  name,
  disabled = false,
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { name: fieldName, value, onChange } }) => (
      <FormControl isInvalid={!!errors[name]?.message}>
        <Checkbox
          id={fieldName}
          name={fieldName}
          size="lg"
          isChecked={!value}
          variant="outline"
          width="100%"
          onChange={e => onChange(!e.currentTarget.checked)}
          isDisabled={disabled}
        >
          Send email notifications to applicant
        </Checkbox>
        <FormErrorMessage>{errors[name]?.message}</FormErrorMessage>
      </FormControl>
    )}
  />
);
